"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SALES_DEBIT_NOTE_SERIES = exports.SALES_CREDIT_NOTE_SERIES = void 0;
exports.invoiceOpenBalance = invoiceOpenBalance;
exports.priceSalesNote = priceSalesNote;
exports.checkSalesNote = checkSalesNote;
exports.checkInvoiceCancellation = checkInvoiceCancellation;
exports.salesNoteIntent = salesNoteIntent;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const invoice_1 = require("./invoice");
/** سلسلتا الإشعارات — مستقلتان عن الفواتير وعن بعضهما، والصيغة من إعداد الترقيم. */
exports.SALES_CREDIT_NOTE_SERIES = 'CN';
exports.SALES_DEBIT_NOTE_SERIES = 'DN';
/**
 * متبقى الفاتورة: الإجمالى + المدينة − الدائنة − المخصص، والملغاة صفر — قيدها العكسى
 * أعاد ذمتها كاملة، ولا تحصيل ولا إشعار عليها أصلاً (شرط الإلغاء).
 */
function invoiceOpenBalance(position) {
    if (position.cancelled)
        return money_1.Money.zero();
    return money_1.Money.of(position.total)
        .add(position.debitNotes)
        .sub(position.creditNotes)
        .sub(position.paid);
}
/**
 * تسعير الإشعار بنفس قاعدة الفاتورة (صافى السطر مقربًا ثم ضريبته ثم الجمع). الضريبة
 * تُحسب بقاعدة نافذة فى تاريخ الفاتورة الأصلية لا تاريخ الإشعار: الإشعار يصحح فاتورة
 * صدرت بنسبتها — `BLOCKED-TAX` حتى يؤكد مستشار الضرائب ذلك للإشعار المدين خاصة.
 */
function priceSalesNote(lines, dates) {
    const issues = [];
    if (dates.noteDate < dates.invoiceDate) {
        issues.push({
            code: 'NOTE_BEFORE_INVOICE',
            message: 'note date ' + dates.noteDate + ' is before invoice date ' + dates.invoiceDate,
        });
    }
    const priced = (0, invoice_1.priceInvoice)(lines, { issueDate: dates.invoiceDate, dueDate: dates.invoiceDate });
    if (!priced.ok)
        issues.push(...priced.issues);
    if (issues.length > 0 || !priced.ok)
        return { ok: false, issues };
    return priced;
}
/**
 * حدود الإشعار على فاتورته. كل المشاكل معًا:
 *  - لا إشعار على فاتورة ملغاة.
 *  - مجموع الدائنة (بالضريبة) لا يتجاوز إجمالى الفاتورة ومديناتها.
 *  - ولا يتجاوز متبقيها: إشعار دائن على فاتورة محصّلة يعنى ردًّا للعميل أو رصيدًا له،
 *    وكلاهما غير معتمد بعد (`BLOCKED-ACCOUNTING`) — فيُرفض بدل أن يصير المتبقى سالبًا.
 */
function checkSalesNote(kind, noteTotal, invoice) {
    const issues = [];
    if (invoice.cancelled) {
        issues.push({ code: 'INVOICE_CANCELLED', message: 'invoice is cancelled' });
        return issues;
    }
    if (kind === 'credit') {
        const ceiling = money_1.Money.of(invoice.total).add(invoice.debitNotes);
        if (money_1.Money.of(invoice.creditNotes).add(noteTotal).gt(ceiling)) {
            issues.push({
                code: 'CREDIT_NOTE_EXCEEDS_INVOICE',
                message: 'credit notes would exceed the invoice total including tax and debit notes (' +
                    ceiling.toFixed(2) +
                    ')',
            });
        }
        const open = invoiceOpenBalance(invoice);
        if (money_1.Money.of(noteTotal).gt(open)) {
            issues.push({
                code: 'CREDIT_NOTE_EXCEEDS_OPEN_BALANCE',
                message: 'credit note exceeds the open balance of the invoice (' + open.toFixed(2) + ')',
            });
        }
    }
    return issues;
}
/** شروط الإلغاء — الفترة المقفلة يرفضها محرك الترحيل نفسه بـ `PERIOD_CLOSED`. */
function checkInvoiceCancellation(invoice, cancellationDate) {
    const issues = [];
    if (invoice.cancelled) {
        issues.push({ code: 'INVOICE_ALREADY_CANCELLED', message: 'invoice is already cancelled' });
        return issues;
    }
    if (money_1.Money.of(invoice.paid).isPositive()) {
        issues.push({
            code: 'INVOICE_HAS_RECEIPTS',
            message: 'invoice has receipts allocated to it; cancellation would orphan them',
        });
    }
    if (invoice.noteCount > 0) {
        issues.push({
            code: 'INVOICE_HAS_NOTES',
            message: 'invoice has credit or debit notes; cancel by a credit note instead',
        });
    }
    if (cancellationDate < invoice.issueDate) {
        issues.push({
            code: 'CANCELLATION_BEFORE_INVOICE',
            message: 'cancellation date ' + cancellationDate + ' is before invoice date ' + invoice.issueDate,
        });
    }
    return issues;
}
/**
 * قيد الإشعار.
 *
 * المدين صورة فاتورة الخدمة: مدين العملاء، دائن الإيراد وضريبة المخرجات.
 *
 * الدائن عكسها: مدين مردودات المبيعات (أو حساب إيراد كل سطر حسب الإعداد) بالصافى،
 * ومدين ضريبة المخرجات بأثر قاعدة كل سطر، ودائن العملاء بالإجمالى على الفاتورة.
 */
function salesNoteIntent(ctx, input) {
    if (input.kind === 'debit') {
        return {
            ...(0, invoice_1.serviceInvoiceIntent)(ctx, { ...input, invoice: input.note }),
            sourceType: 'sales_debit_note',
        };
    }
    const { note } = input;
    const lines = [];
    for (const returned of (0, invoice_1.revenueLines)(note, (line) => input.creditNoteAccount === 'original_revenue' ? line.account : ctx.accounts.salesReturns)) {
        if (returned.amount.isPositive()) {
            lines.push((0, document_rules_1.debitLine)(returned.account, returned.amount, ctx.currency, { tax: returned.tax }));
        }
    }
    for (const line of note.lines) {
        if (line.tax && line.tax.amount.isPositive()) {
            const account = input.outputTaxAccountOf?.({ ruleId: line.tax.snapshot.ruleId }) ?? ctx.accounts.outputTax;
            lines.push((0, document_rules_1.debitLine)(account, line.tax.amount, ctx.currency, { tax: line.tax.snapshot }));
        }
    }
    lines.push((0, document_rules_1.creditLine)(ctx.accounts.receivable, note.total, ctx.currency, {
        counterparty: { type: 'customer', id: input.customerId, allocationRef: input.invoiceId },
    }));
    return {
        companyId: ctx.companyId,
        sourceType: 'sales_credit_note',
        sourceId: input.sourceId,
        postingDate: input.postingDate,
        idempotencyKey: input.idempotencyKey,
        reference: input.reference ?? null,
        memo: input.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
//# sourceMappingURL=notes.js.map